import { useEffect, useState } from "react";
import axios from "axios";
import { Home, ExternalLink, Phone, Building2, MapPin, Info, RefreshCw } from "lucide-react";
import { MapLink } from "@/components/MapLink";
import { usePageMeta } from "@/utils/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Manuella reservobjekt — visas bara om motsvarande adress inte redan finns
// dynamiskt från Strömsunds Hyresbostäder. Tas inte bort blint, jfr dedup.
const HYRESRATTER_MANUELL = [
  { adress: "Strandvägen 2 C", rum: "2 rum", storlek: "66 m²", hyra: "5 821 kr/mån", ort: "Backe", status: "Ledig nu" },
  { adress: "Centrumvägen 2 A", rum: "3 rum", storlek: "73 m²", hyra: "6 216 kr/mån", ort: "Backe", status: "Ledig nu" },
  { adress: "Läkarvägen 16", rum: "3 rum", storlek: "86 m²", hyra: "7 540 kr/mån", ort: "Backe", status: "Ledig nu" },
  { adress: "Torgvägen 4", rum: "3 rum", storlek: "80 m²", hyra: "7 022 kr/mån", ort: "Backe", status: "Ledig nu" },
];

const HUS_TILL_SALU = [
  { adress: "Ringvägen 16, Rossön", pris: "480 000 kr", storlek: "85 + 58 m²", rum: "4 rum", tomt: "1 265 m²", beskrivning: "Villa i mycket fint skick i Rossön. Fantastiskt läge med storslagen natur, skoterleder, sjöar och vandringsleder. Visning lördag 2 maj kl 11:00. Fastighetsbyrån Strömsund.", status: "Ny!", url: "https://www.hemnet.se/bostad/villa-4rum-rosson-stromsunds-kommun-ringvagen-16-21704216" },
  { adress: "Lasarettsvägen 16, Backe", pris: "500 000 kr", storlek: "145 + 130 m²", rum: "7 rum", tomt: "1 560 m²", beskrivning: "Villa med fd verksamhetslokal och uteplats. Nära Fjällsjöälven med utsikt mot Backesjön. Båtplats. Lämplig för boende + verksamhet/uthyrning. 15 dagar på Hemnet.", status: "Till salu", url: "https://www.hemnet.se/bostad/villa-7rum-backe-stromsunds-kommun-lasarettsvagen-16-21697160" },
];

const SÅLDA_NYLIGEN = [
  { adress: "Tallvägen 9, Backe", pris: "350 000 kr", storlek: "125 + 107 m²", rum: "6 rum", tomt: "1 303 m²" },
  { adress: "Borgs väg 8, Backe", pris: "350 000 kr", storlek: "116 + 55 m²", rum: "5 rum", tomt: "1 245 m²" },
  { adress: "Hocksjö 130, Rossön", pris: "220 000 kr", storlek: "91 + 56 m²", rum: "5 rum", tomt: "9 020 m²" },
];

const PLATTFORMAR = [
  { namn: "Strömsunds Hyresbostäder", beskrivning: "Kommunalt bostadsbolag. Hyresrätter i Backe och Strömsund. Bostadskö online.", url: "https://bostad.stromsundshyresbostader.se/ledigt/lagenhet", telefon: "0670-169 55", typ: "hyresrätt" },
  { namn: "Hemnet", beskrivning: "Sveriges största bostadssajt. Hus, lägenheter och tomter till salu i hela kommunen.", url: "https://www.hemnet.se/till-salu/stromsunds-kommun", typ: "köp" },
  { namn: "Bofrid", beskrivning: "Andrahandsuthyrning utan bostadskö. Kan ha lägenheter i Backe och Rossön.", url: "https://bofrid.se/hyra-bostad/jamtlands-lan/stromsund/backe", typ: "hyresrätt" },
  { namn: "Bovision", beskrivning: "Samlar hyresbostäder från flera hyresvärdar.", url: "https://bovision.se/sverige/hyra/lagenhet/jamtland/stromsund/", typ: "hyresrätt" },
  { namn: "Boplatssverige", beskrivning: "Rikstäckande bostadsportal med hyresvärdar.", url: "https://www.boplatssverige.se/Lediga-lagenheter/Hyresvard/Str%C3%B6msunds-Hyresbost%C3%A4der", typ: "hyresrätt" },
  { namn: "Strömsunds kommun (bostad)", beskrivning: "Information om bostadsmarknaden, lediga tomter och boendemöjligheter.", url: "https://www.stromsund.se/1945.html", telefon: "0670-161 00", typ: "info" },
];

// Normalisera adress för dedup: små bokstäver, ta bort mellanslag/skiljetecken
function normAddress(s) {
  if (!s) return "";
  return s.toLowerCase().replace(/[\s,.\-/]/g, "").trim();
}

function fmtKr(n) {
  if (n === null || n === undefined) return null;
  return `${Number(n).toLocaleString("sv-SE")} kr/mån`;
}

function fmtDateOnly(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("sv-SE"); } catch { return iso; }
}

export default function BostaderIBygden() {
  usePageMeta({
    title: "Bostad – vidalv.live",
    description: "Lediga hyresrätter och hus till salu i Backe, Rossön och bygden. Aktuella objekt direkt från Strömsunds Hyresbostäder samt marknadsöverblick från Hemnet och Bofrid.",
    path: "/bostader",
  });
  const [dynItems, setDynItems] = useState([]);
  const [lastChecked, setLastChecked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bostader`, { timeout: 12000 });
        if (cancelled) return;
        setDynItems(Array.isArray(data?.items) ? data.items : []);
        setLastChecked(data?.last_checked_at || null);
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Dedup: dynamisk officiell HSS-träff har företräde framför manuell lista.
  const dynAddressKeys = new Set(dynItems.map((it) => normAddress(it.address)));
  const manualFallback = HYRESRATTER_MANUELL.filter(
    (m) => !dynAddressKeys.has(normAddress(m.adress))
  );

  return (
    <div data-testid="bostader-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Bostad</p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-2">Boende i bygden</h1>
      <p className="text-sm text-bark mb-6 max-w-2xl leading-relaxed">
        Lediga hyresrätter, hus till salu och bostadsplattformar för Backe, Rossön och omnejd.
      </p>

      {/* Källanmärkning (live) */}
      <div
        data-testid="bostader-source-note"
        className="mb-8 bg-bone-alt border border-warm-border rounded-sm p-3 sm:p-4 flex items-start gap-2"
      >
        <Info size={16} className="text-pine shrink-0 mt-0.5" />
        <p className="text-xs sm:text-sm text-bark leading-relaxed">
          Uppgifter kan ändras. Kontrollera alltid aktuell information hos Strömsunds Hyresbostäder, mäklare eller källa.
          {lastChecked && (
            <>
              {" "}<span className="text-muted-foreground">Senast kontrollerat: {fmtDateOnly(lastChecked)}.</span>
            </>
          )}
        </p>
      </div>

      {/* Hyresrätter – dynamiskt via Strömsunds Hyresbostäder */}
      <section data-testid="hyresratter-section" className="mb-12">
        <div className="flex items-center gap-2 mb-3">
          <Building2 size={22} className="text-pine" />
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink">Hyresrätter i Backe och Rossön</h2>
          {loading && (
            <RefreshCw size={14} className="animate-spin text-muted-foreground" data-testid="hyresratter-loading" />
          )}
        </div>
        <p className="text-base text-muted-foreground mb-4">
          Direkt från Strömsunds Hyresbostäder — uppdateras automatiskt. Endast objekt i
          <strong> Backe</strong> och <strong>Rossön</strong> visas här (övriga orter syns i bostadskön hos källan).
          Anmäl dig i bostadskön för att söka.
          Kontakt: <a href="tel:067016955" className="text-falu hover:text-falu-hover no-underline">0670-169 55</a> (telefontid 8&ndash;16).
        </p>

        {/* Dynamiska Strömsund-träffar */}
        {!loading && dynItems.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="hyresratter-dynamic">
            {dynItems.map((it) => (
              <a
                key={it.id || it.detail_url}
                href={it.detail_url || it.source_url || "https://bostad.stromsundshyresbostader.se/ledigt/lagenhet"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`hyresratt-dyn-${it.id || ""}`}
                className="block bg-parchment border border-warm-border rounded-sm p-4 hover:border-pine/60 transition-colors no-underline"
              >
                <div className="flex items-center justify-between mb-1 gap-2">
                  <h3 className="font-heading text-base font-semibold text-ink truncate">{it.address}</h3>
                  {fmtKr(it.rent) && (
                    <span className="text-sm font-semibold text-pine shrink-0">{fmtKr(it.rent)}</span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-muted-foreground truncate">
                    {[
                      it.rooms ? `${it.rooms} rum` : null,
                      it.living_area ? `${it.living_area} m²` : null,
                      it.area || null,
                    ].filter(Boolean).join(" · ")}
                  </p>
                  <span className="text-xs font-semibold bg-pine/10 text-pine px-2 py-0.5 rounded shrink-0">
                    {it.available_from ? `Inflytt: ${it.available_from}` : "Ledig"}
                  </span>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Manuella reservobjekt (visas bara om de inte redan finns dynamiskt) */}
        {!loading && manualFallback.length > 0 && (
          <div className="mt-4">
            {dynItems.length > 0 && (
              <p className="text-xs text-muted-foreground mb-2" data-testid="manual-fallback-label">
                Ytterligare tidigare publicerade objekt (verifiera direkt hos Strömsunds Hyresbostäder):
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="hyresratter-manual">
              {manualFallback.map((h, i) => (
                <div key={`m-${i}`} className="bg-parchment/60 border border-warm-border rounded-sm p-4">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-heading text-base font-semibold text-ink">{h.adress}</h3>
                    <span className="text-sm font-semibold text-pine">{h.hyra}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">{h.rum} · {h.storlek} · {h.ort}</p>
                    <span className="text-xs font-semibold bg-bone-alt text-bark/70 px-2 py-0.5 rounded">Tidigare</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tomt resultat / fel */}
        {!loading && dynItems.length === 0 && manualFallback.length === 0 && !loadError && (
          <p className="text-sm text-muted-foreground" data-testid="hyresratter-empty">
            Inga lediga lägenheter just nu hos Strömsunds Hyresbostäder.
          </p>
        )}
        {loadError && (
          <p className="text-sm text-falu" data-testid="hyresratter-error">
            Kunde inte hämta aktuella lägenheter just nu. Kontrollera direkt hos källan.
          </p>
        )}

        <a
          href="https://bostad.stromsundshyresbostader.se/ledigt/lagenhet"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-4 text-base font-semibold text-falu hover:text-falu-hover no-underline"
          data-testid="hyresratter-source-link"
        >
          <ExternalLink size={16} /> Visa alla på Strömsunds Hyresbostäder
        </a>
      </section>

      {/* Marknadsöverblick — manuell, sekundär */}
      <section data-testid="marknadsoverblick-section" className="mb-12">
        <div className="flex items-center gap-2 mb-2">
          <Home size={22} className="text-pine" />
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink">Marknadsöverblick (manuell)</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          Sekundär marknadsinformation från Hemnet och mäklare — hämtas <strong>inte</strong> automatiskt
          och uppdateras manuellt. Inga bilder eller fulla annonstexter visas här; klicka för fullständiga uppgifter hos källan.
        </p>

        {/* Hus till salu */}
        <div className="flex flex-col gap-3" data-testid="hus-till-salu-list">
          {HUS_TILL_SALU.map((h, i) => (
            <div key={i} className="bg-parchment border border-warm-border rounded-sm p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading text-base font-semibold text-ink">{h.adress}</h3>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${h.status === "Ny!" ? "bg-pine/15 text-pine" : "bg-falu/10 text-falu"}`}>{h.status}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">{h.rum} · {h.storlek} · Tomt: {h.tomt}</p>
                  <p className="text-sm text-muted-foreground mb-2">{h.beskrivning}</p>
                  <div className="flex items-center gap-3">
                    {h.url && <a href={h.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-falu hover:text-falu-hover no-underline"><ExternalLink size={13} /> Visa på Hemnet</a>}
                    <MapLink name={h.adress} />
                  </div>
                </div>
                <span className="text-lg font-heading font-semibold text-pine shrink-0">{h.pris}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Referenspriser */}
        {SÅLDA_NYLIGEN.length > 0 && (
          <div className="mt-6" data-testid="referenspriser">
            <h3 className="font-heading text-lg font-semibold text-ink mb-1">Referenspriser (slutpriser, ej automatiskt verifierade)</h3>
            <p className="text-xs text-muted-foreground mb-3">Manuellt nedtecknade exempel från Hemnet — ingen automatisk status om "såld" eller "uthyrd".</p>
            <div className="flex flex-col gap-2">
              {SÅLDA_NYLIGEN.map((h, i) => (
                <div key={i} className="bg-bone-alt rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-ink">{h.adress}</p>
                    <p className="text-xs text-muted-foreground">{h.rum} · {h.storlek} · Tomt: {h.tomt}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-base font-heading font-semibold text-muted-foreground">{h.pris}</span>
                    <span className="block text-xs text-muted-foreground">Slutpris (referens)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <a href="https://www.hemnet.se/till-salu/stromsunds-kommun/backe" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-base font-semibold text-falu hover:text-falu-hover no-underline">
          <ExternalLink size={16} /> Visa alla på Hemnet
        </a>
      </section>

      {/* Rossön boende */}
      <section data-testid="rosson-boende-section" className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <MapPin size={22} className="text-pine" />
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink">Boende i Rossön</h2>
        </div>
        <div className="bg-parchment border border-warm-border rounded-sm p-5">
          <p className="text-base text-muted-foreground mb-3">
            Sekundära källor för boende i Rossön (manuellt sammanställda):
          </p>
          <ul className="text-base text-muted-foreground flex flex-col gap-2 list-disc pl-5">
            <li><strong>Rossö Center</strong> &ndash; Trygghetsboende för äldre. Bodumsvägen 2, Rossön. Ansök via <a href="https://www.stromsund.se/10807.html" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Strömsunds kommuns e-tjänst</a>.</li>
            <li><strong>Bofrid</strong> &ndash; Andrahandsuthyrning utan kö. <a href="https://bofrid.se/hyra-bostad/jamtlands-lan/stromsund/rosson" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Sök Rossön på Bofrid</a>.</li>
            <li><strong>Hemnet</strong> &ndash; Hus och tomter till salu. <a href="https://www.hemnet.se/till-salu/stromsunds-kommun" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Sök Rossön på Hemnet</a>.</li>
            <li><strong>Anslagstavlan</strong> &ndash; Lokala uthyrningar och bostadsbytaren. Fråga grannar eller skriv på anslagstavlan.</li>
          </ul>
        </div>
      </section>

      {/* Plattformar */}
      <section data-testid="plattformar-section" className="mb-10">
        <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-4">Bostadsplattformar</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PLATTFORMAR.map((p, i) => (
            <div key={i} className="bg-parchment border border-warm-border rounded-sm p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-heading text-base font-semibold text-ink">{p.namn}</h3>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${p.typ === "köp" ? "bg-falu/10 text-falu" : p.typ === "hyresrätt" ? "bg-pine/10 text-pine" : "bg-bone-alt text-muted-foreground"}`}>
                  {p.typ === "köp" ? "Köp" : p.typ === "hyresrätt" ? "Hyra" : "Info"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-2">{p.beskrivning}</p>
              <div className="flex items-center gap-3">
                <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-falu hover:text-falu-hover no-underline">
                  <ExternalLink size={13} /> Besök
                </a>
                {p.telefon && (
                  <a href={`tel:${p.telefon.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 text-sm text-pine hover:text-pine-hover no-underline">
                    <Phone size={13} /> {p.telefon}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="pt-6 border-t border-warm-border">
        <p className="text-xs text-muted-foreground">
          Källor: <a href="https://bostad.stromsundshyresbostader.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">stromsundshyresbostader.se</a> (automatisk uppdatering), <a href="https://www.hemnet.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">hemnet.se</a> (manuell referens), <a href="https://bofrid.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">bofrid.se</a>, <a href="https://www.stromsund.se/1945.html" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">stromsund.se</a>.
          {lastChecked && (
            <> Senast automatiskt kontrollerat: <span data-testid="footer-last-checked">{fmtDateOnly(lastChecked)}</span>.</>
          )}
          {" "}Uppgifter kan ändras &ndash; kontrollera alltid direkt hos respektive källa.
        </p>
      </footer>
    </div>
  );
}
